import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

/**
 * Names a project's architecture and saves it as a reusable, user-owned template.
 *
 * Deliberately mirrors RenameDialog's shape (local draft state, Enter to submit,
 * disabled-when-empty) rather than inventing a third dialog idiom — see the
 * shared-dialog note in ProjectOutput.jsx.
 */
export default function SaveTemplateDialog({ open, defaultName = "", onCancel, onSubmit }) {
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Re-seed the draft each time the dialog opens so a previous attempt's text
  // doesn't linger, and so the name tracks the project's current title.
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setDescription("");
      setSubmitting(false);
    }
  }, [open, defaultName]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: trimmed, description: description.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    background: "var(--color-background-secondary)",
    border: "1px solid var(--color-border-primary)",
    borderRadius: 8,
    color: "var(--color-text-primary)",
    fontSize: 13.5,
    padding: "9px 11px",
    outline: "none",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel?.()}>
      <DialogContent
        data-testid="save-template-dialog"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription>
            Saves this project's services and connections so you can start a new project
            from them instantly without another architecture run.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Name</span>
            <input
              data-testid="save-template-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="e.g. Standard internal API"
              style={inputStyle}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
              Description <span style={{ opacity: 0.6 }}>(optional)</span>
            </span>
            <input
              data-testid="save-template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="When would you reach for this?"
              style={inputStyle}
            />
          </label>
        </div>

        <DialogFooter>
          <button
            data-testid="save-template-cancel"
            onClick={onCancel}
            className="motion-micro"
            style={{
              fontSize: 13,
              height: 36,
              padding: "0 14px",
              borderRadius: 8,
              border: "1px solid var(--color-border-primary)",
              background: "transparent",
              color: "var(--color-text-secondary)",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            data-testid="save-template-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="motion-micro"
            style={{
              fontSize: 13,
              height: 36,
              padding: "0 14px",
              borderRadius: 8,
              border: "none",
              background: "var(--color-accent)",
              color: "#0a0a0a",
              cursor: canSubmit ? "pointer" : "not-allowed",
              opacity: canSubmit ? 1 : 0.6,
            }}
          >
            {submitting ? "Saving…" : "Save template"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
